// [PSR-005 FIX — MAY 2026]: Party password hashing helper.
//
// Pre-fix: party password stored as plaintext in `metadata.password`. Any
// member who joined could read the password from Firebase (the .read rule
// gates on memberUids, but once joined the read is allowed). This isn't a
// strong leak (the joiner already knows the password they typed) but it's
// hygiene + defense-in-depth.
//
// Threat model honesty: this hash is NOT a strong access control. A malicious
// party member can read the hash and brute-force offline. The salt is the
// partyId (unique per party, but predictable). The hash protects against
// casual leakage; it does not protect against a determined attacker. A real
// access-control mechanism would be server-rule-enforced or Firebase Auth
// custom claims. v1.0 takes the hygiene fix; v1.x can revisit.
//
// Algorithm: SHA-256(password + ':' + partyId). The colon delimiter prevents
// trivial collision attacks (e.g., password='abc', partyId='123' produces
// the same digest as password='abc1', partyId='23' WITHOUT the delimiter).

/**
 * Hash a party password with the partyId as salt.
 *
 * @param {string} password - the user-typed password (will be trimmed)
 * @param {string} partyId - the party's id (used as salt)
 * @returns {Promise<string>} hex-encoded SHA-256 digest
 */
/* global globalThis */ // standard global; declared so eslint no-undef doesn't fail the dev compile
export async function hashPartyPassword(password, partyId) {
  const trimmed = String(password || '').trim();
  const salt = String(partyId || '');
  if (!trimmed) return '';
  const text = `${trimmed}:${salt}`;
  const data = new TextEncoder().encode(text);
  const cryptoApi = (typeof globalThis !== 'undefined' && globalThis.crypto)
    || (typeof window !== 'undefined' && window.crypto)
    || null;
  if (!cryptoApi || !cryptoApi.subtle || !cryptoApi.subtle.digest) {
    throw new Error('[partyPasswordHash] Web Crypto API not available');
  }
  const digest = await cryptoApi.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Verify a typed password against a stored hash. If `legacyPlaintext` is
 * provided (existing parties pre-hash), it's matched directly; new parties
 * only carry `storedHash`.
 *
 * @param {string} typedPassword - what the user typed
 * @param {string} partyId - the party's id (salt for the hash path)
 * @param {string|null|undefined} storedHash - metadata.passwordHash, if any
 * @param {string|null|undefined} legacyPlaintext - metadata.password, if any (legacy parties)
 * @returns {Promise<boolean>}
 */
export async function verifyPartyPassword(typedPassword, partyId, storedHash, legacyPlaintext) {
  const trimmed = String(typedPassword || '').trim();
  if (storedHash) {
    if (!trimmed) return false;
    const computed = await hashPartyPassword(trimmed, partyId);
    return computed === storedHash;
  }
  if (legacyPlaintext) {
    return trimmed === legacyPlaintext;
  }
  return true; // no password set on either field — open party
}
